import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { ArrowLeft, Phone, Plus, Clock, FileText } from "lucide-react"
import { format } from "date-fns"
import { useFetchPatient } from "@/services/query/patients.query"
import { useFetchPatientReports } from "@/services/query/reports.query"
import { useFetchAppointments } from "@/services/query/appointments.query"
import { AddReportDialog } from "./AddReportDialog"
import { FileViewer } from "@/components/FileViewer"
import AppointmentCard from "@/pages/appointments/components/AppointmentCard"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { useToast } from "@/components/ui/use-toast"
import {
  useUpdateAppointmentStatus,
  useDeleteAppointment,
  useUpdateAppointment,
  useCompleteAppointment,
  useCreateAppointment,
} from "@/services/query/appointments.query"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"
import { EditAppointmentDialog } from "@/components/appointments/EditAppointmentDialog"
import { CompleteAppointmentDialog } from "@/components/appointments/CompleteAppointmentDialog"
import { AddAppointmentDialog } from "@/components/appointments/AddAppointmentDialog"
import { Switch } from "@/components/ui/switch"

function PatientDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
  const [reportsPagination, setReportsPagination] = useState({
    offset: 0,
    limit: 10,
  })
  const [appointmentsPagination, setAppointmentsPagination] = useState({
    offset: 0,
    limit: 5,
  })
  const { toast } = useToast()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [editingAppointment, setEditingAppointment] = useState(null)
  const [completingAppointment, setCompletingAppointment] = useState(null)
  const [aiFollowUp, setAiFollowUp] = useState(false)
  const [showAddAppointment, setShowAddAppointment] = useState(false)
  const createAppointmentMutation = useCreateAppointment()

  const { data: patient } = useFetchPatient(id)

  const { data: reportsData } = useFetchPatientReports(
    id,
    reportsPagination.offset,
    reportsPagination.limit
  )

  const { data: appointmentsData, isLoading: isLoadingAppointments } =
    useFetchAppointments(
      appointmentsPagination.offset,
      appointmentsPagination.limit,
      "",
      null,
      null,
      null,
      id
    )

  const deleteAppointmentMutation = useDeleteAppointment()
  const updateStatusMutation = useUpdateAppointmentStatus()
  const updateAppointmentMutation = useUpdateAppointment()
  const completeAppointmentMutation = useCompleteAppointment()

  const handleAppointmentStatusUpdate = async (appointment, newStatus) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: appointment.id,
        status: newStatus === 1 ? "APPROVED" : "REJECTED",
      })
      toast({
        title: "Success",
        description: `Appointment ${newStatus === 1 ? "accepted" : "rejected"} successfully`,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${newStatus === 1 ? "accept" : "reject"} appointment`,
        variant: "destructive",
      })
    }
  }

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return

    try {
      await deleteAppointmentMutation.mutateAsync(selectedAppointment.id)
      toast({
        title: "Success",
        description: "Appointment deleted successfully",
      })
      setDeleteDialogOpen(false)
      setSelectedAppointment(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete appointment",
        variant: "destructive",
      })
    }
  }

  const handleEditAppointment = async (data) => {
    try {
      await updateAppointmentMutation.mutateAsync({
        ...data,
        appointment_status: "PENDING",
        doctor_id: editingAppointment.doctor_id,
      })
      toast({
        title: "Success",
        description: "Appointment updated successfully",
      })
      setEditingAppointment(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update appointment",
        variant: "destructive",
      })
    }
  }

  const handleCompleteAppointment = async (file) => {
    try {
      await completeAppointmentMutation.mutateAsync({
        id: completingAppointment.id,
        file,
      })
      toast({
        title: "Success",
        description: "Appointment marked as completed",
      })
      setCompletingAppointment(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to complete appointment",
        variant: "destructive",
      })
    }
  }

  const handleCreateAppointment = async (data) => {
    try {
      await createAppointmentMutation.mutateAsync(data)
      toast({
        title: "Success",
        description: "Appointment scheduled successfully",
      })
      setShowAddAppointment(false)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to schedule appointment",
        variant: "destructive",
      })
    }
  }

  if (!patient) {
    return <div>No Info Available</div>
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <Button
          variant="ghost"
          className="hover:bg-blue-50 w-fit mb-6"
          onClick={() => navigate("/patients")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Patients
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8 lg:min-h-[calc(100vh-8rem)]">
          {/* Profile Section */}
          <Card className="lg:col-span-2 lg:h-[calc(100vh-8rem)] overflow-auto">
            <CardHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10 pb-4 border-b">
              <CardTitle className="text-xl sm:text-2xl">
                {patient.user_name}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center text-sm sm:text-base">
                <Phone className="h-4 w-4 mr-2 text-blue-500" />
                <span>{patient.phone_number}</span>
              </div>
              <div className="flex items-center text-sm sm:text-base">
                <Clock className="h-4 w-4 mr-2 text-blue-500" />
                <span>
                  Last updated: {format(new Date(patient.modified_at), "PPP")}
                </span>
              </div>
              <div className="flex items-center justify-between mb-4 mt-6 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">AI Automated FollowUp</h4>
                  <p className="text-xs text-gray-500">Enable AI assistance</p>
                </div>
                <Switch checked={aiFollowUp} onCheckedChange={setAiFollowUp} />
              </div>
              {aiFollowUp && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-blue-600 dark:text-blue-400 text-sm font-medium animate-pulse">
                    🤖 AI Automated FollowUp Coming Soon...
                  </p>
                </div>
              )}
              <div className="mt-4">
                <h3 className="text-sm font-semibold mb-2">Summary</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {patient.summary || "No summary available"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Section */}
          <div className="lg:col-span-4 lg:h-[calc(100vh-8rem)] flex flex-col">
            <Tabs
              defaultValue="reports"
              className="flex-1 flex flex-col h-full"
            >
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="reports" className="text-sm sm:text-base">
                  Reports
                </TabsTrigger>
                <TabsTrigger
                  value="appointments"
                  className="text-sm sm:text-base"
                >
                  Appointments
                </TabsTrigger>
              </TabsList>

              <TabsContent value="reports" className="flex-1 mt-5 h-full">
                <Card className="h-full flex flex-col">
                  <CardHeader className="border-b shrink-0">
                    <div className="flex flex-row items-center justify-between space-y-0">
                      <div>
                        <CardTitle>Medical Reports</CardTitle>
                        <CardDescription>View patient reports</CardDescription>
                      </div>
                      <Button onClick={() => setIsReportDialogOpen(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Report
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full">
                      <div className="p-6">
                        <div className="grid grid-cols-1 gap-4">
                          {reportsData?.reports.map((report) => (
                            <Card key={report.id}>
                              <CardHeader>
                                <CardTitle className="text-lg">
                                  {report.title}
                                </CardTitle>
                                <CardDescription>
                                  {format(new Date(report.created_at), "PPP")}
                                </CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-4">
                                  <p className="text-sm text-gray-600">
                                    {report.details}
                                  </p>
                                  {report.file_path && (
                                    <FileViewer
                                      filePath={report.file_path}
                                      fileType={report.file_type}
                                      fileName={report.file_name}
                                    />
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="appointments" className="flex-1 mt-5 h-full">
                <Card className="h-full flex flex-col">
                  <CardHeader className="border-b shrink-0">
                    <div className="flex flex-row items-center justify-between space-y-0">
                      <div>
                        <CardTitle>Appointments History</CardTitle>
                        <CardDescription>
                          View patient appointments
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => setShowAddAppointment(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Appointment
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full">
                      <div className="p-6">
                        <div className="grid grid-cols-1 gap-4">
                          {isLoadingAppointments ? (
                            <div className="text-center py-4">
                              Loading appointments...
                            </div>
                          ) : appointmentsData?.appointments.length === 0 ? (
                            <div className="text-center py-4 text-gray-500">
                              No appointments found
                            </div>
                          ) : (
                            appointmentsData?.appointments.map(
                              (appointment) => (
                                <AppointmentCard
                                  key={appointment.id}
                                  appointment={appointment}
                                  onAccept={() =>
                                    handleAppointmentStatusUpdate(
                                      appointment,
                                      1
                                    )
                                  }
                                  onReject={() =>
                                    handleAppointmentStatusUpdate(
                                      appointment,
                                      2
                                    )
                                  }
                                  onDelete={() => {
                                    setSelectedAppointment(appointment)
                                    setDeleteDialogOpen(true)
                                  }}
                                  onEdit={() =>
                                    setEditingAppointment(appointment)
                                  }
                                  onComplete={() =>
                                    setCompletingAppointment(appointment)
                                  }
                                />
                              )
                            )
                          )}
                        </div>
                        {appointmentsData?.pagination.totalPages > 1 && (
                          <div className="mt-6 flex justify-center">
                            <Pagination>
                              <PaginationContent>
                                <PaginationItem>
                                  <PaginationPrevious
                                    onClick={() =>
                                      setAppointmentsPagination((prev) => ({
                                        ...prev,
                                        offset: Math.max(
                                          0,
                                          prev.offset - prev.limit
                                        ),
                                      }))
                                    }
                                    disabled={
                                      appointmentsPagination.offset === 0
                                    }
                                  />
                                </PaginationItem>

                                {[
                                  ...Array(
                                    appointmentsData.pagination.totalPages
                                  ),
                                ].map((_, idx) => (
                                  <PaginationItem key={idx}>
                                    <PaginationLink
                                      onClick={() =>
                                        setAppointmentsPagination((prev) => ({
                                          ...prev,
                                          offset: idx * prev.limit,
                                        }))
                                      }
                                      isActive={
                                        appointmentsPagination.offset ===
                                        idx * appointmentsPagination.limit
                                      }
                                    >
                                      {idx + 1}
                                    </PaginationLink>
                                  </PaginationItem>
                                ))}

                                <PaginationItem>
                                  <PaginationNext
                                    onClick={() =>
                                      setAppointmentsPagination((prev) => ({
                                        ...prev,
                                        offset: prev.offset + prev.limit,
                                      }))
                                    }
                                    disabled={
                                      appointmentsPagination.offset +
                                        appointmentsPagination.limit >=
                                      appointmentsData.pagination.total
                                    }
                                  />
                                </PaginationItem>
                              </PaginationContent>
                            </Pagination>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <AddReportDialog
        open={isReportDialogOpen}
        onOpenChange={setIsReportDialogOpen}
        patientId={id}
      />

      {editingAppointment && (
        <EditAppointmentDialog
          appointment={editingAppointment}
          open={!!editingAppointment}
          onOpenChange={(open) => !open && setEditingAppointment(null)}
          onSave={handleEditAppointment}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              appointment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAppointment}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {completingAppointment && (
        <CompleteAppointmentDialog
          open={true}
          onOpenChange={(open) => !open && setCompletingAppointment(null)}
          onComplete={handleCompleteAppointment}
        />
      )}

      <AddAppointmentDialog
        open={showAddAppointment}
        onOpenChange={setShowAddAppointment}
        patientId={id}
        onSave={handleCreateAppointment}
      />
    </div>
  )
}

export default PatientDetailsPage
